import { Expose } from "class-transformer";

import { AIUsage } from "@admin/components/ai-usage/models/ai-usage.model";
import { Organization } from "@shared/models/organization.model";

import { AuthProvider } from "./types.model";

export enum UserRole {
  SUPER_ADMIN = "SUPER_ADMIN",
  ORGANIZATION_ADMIN = "ORGANIZATION_ADMIN",
  ORGANIZATION_USER = "ORGANIZATION_USER"
}

export enum UserStatus {
  NEW = "NEW",
  REGISTERED = "REGISTERED",
  TEMPORARY = "TEMPORARY"
}

export enum UserType {
  APP_USER = "APP_USER",
  API_USER = "API_USER"
}

export interface ProviderData {
  [key: string]: any;
  login?: string;
  name?: string;
  avatar_url?: string;
  html_url?: string;
  bio?: string;
}

export class User {
  @Expose()
  id!: string;

  @Expose()
  email?: string;

  @Expose()
  username!: string;

  @Expose()
  firstName?: string;

  @Expose()
  lastName?: string;

  @Expose()
  role!: UserRole;

  @Expose()
  userType!: UserType;

  @Expose()
  organizationId!: string;

  @Expose()
  isSuspended!: boolean;

  @Expose()
  status!: UserStatus;

  @Expose()
  emailConfirmed!: boolean;

  @Expose()
  emailVerificationTokenExpiry?: string;

  @Expose()
  createdAt!: string;

  @Expose()
  updatedAt!: string;

  @Expose()
  lastLoginAt?: string;

  @Expose()
  avatarUrl?: string;

  @Expose()
  authProvider!: AuthProvider;

  @Expose()
  organization?: Organization;

  @Expose()
  providerData?: ProviderData;

  @Expose()
  preferences?: {
    defaultAiModelId?: string;
    helpMeCreateAiModelId?: string;
  };

  @Expose()
  aiUsage?: AIUsage;

  // API Client fields (only for userType = API_USER)
  @Expose()
  clientId?: string;

  @Expose()
  clientSecretHash?: string;

  @Expose()
  lastUsedAt?: string;

  @Expose()
  scopes?: any[];

  get avatar(): string {
    return this.avatarUrl || this.providerData?.avatar_url || "";
  }

  get isTemporary(): boolean {
    return this.status === UserStatus.TEMPORARY;
  }

  get isApiUser(): boolean {
    return this.userType === UserType.API_USER;
  }

  get isAppUser(): boolean {
    return this.userType === UserType.APP_USER;
  }

  get maskedClientId(): string {
    if (!this.clientId) return "";
    if (this.clientId.length <= 8) return this.clientId;
    return `${this.clientId.substring(0, 4)}...${this.clientId.substring(this.clientId.length - 4)}`;
  }

  get hasPlan(): boolean {
    return this.organization?.planId !== null;
  }

  get displayName(): string {
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    if (this.firstName) {
      return this.firstName;
    }
    if (this.lastName) {
      return this.lastName;
    }

    return this.username;
  }

  get emailOrUsername(): string {
    return this.email || this.username || "";
  }

  get recipientDisplayName(): string {
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}${this.email ? ` <${this.email}>` : ""}`;
    }
    if (this.username) {
      return `${this.username}${this.email ? ` <${this.email}>` : ""}`;
    }

    return this.email || "";
  }

  constructor(partial?: Partial<User>) {
    if (partial) {
      Object.assign(this, partial);
    }
  }
}
